// The GW Points hero card's derived state — live tier, the
// full-time stamp, the Goalfest live-counter pulse, and the points roll-up. Extracted
// from GameweekGrid.tsx.
import { useEffect, useMemo, useRef, useState } from 'react'
import { computeLiveGameweekTier } from '../../liveGameweekTier'
import { SKILL_TIERS, getSkillTierIndex } from '../../skillTiers'
import {
  SCORE_COUNT_UP_MS,
  hasGwPointsCountUpPlayed,
  markGwPointsCountUpPlayed,
  prefersReducedMotion,
  easeOutCubic,
} from '../../scoreCountUp'
import type { SimGameStatus } from '../../gameweekSimulation'
import type { Game, Prediction } from '../../types'
import type { GameweekStats } from '../../storage'
import type { ScoreLine } from './gameweekPoints'

// GW Points hero: duration of the one-shot "stamp" animation on
// the tier band when the gameweek transitions to full time. Matches
// --motion-slow (tokens.css) so the JS-side class removal lines up with the
// CSS keyframe in GameweekGrid.css.
const STAMP_ANIMATION_MS = 320

// How long the brief scale/opacity pulse plays on each increment of the
// Goalfest live counter under the GW Points card.
const GOALFEST_PULSE_MS = 500

export function useGwPointsHero({
  games,
  predictions,
  actualResults,
  isGameRevealed,
  resultsRevealed,
  simGameStatuses,
  gameweekPointsSoFar,
  goalfestLiveBonus,
  allFixturesResolved,
  stats,
  myUserId,
}: {
  games: Game[]
  predictions: Record<string, Prediction>
  actualResults: Record<string, ScoreLine>
  isGameRevealed: (gameId: string) => boolean
  resultsRevealed: boolean
  simGameStatuses?: Record<string, SimGameStatus>
  gameweekPointsSoFar: number
  goalfestLiveBonus: number
  allFixturesResolved: boolean
  /** The gameweek's real average/highest, fetched from the server — see useGameweekStats. */
  stats: GameweekStats
  /** The signed-in user, so the Highest tile knows when it is pointing at them. */
  myUserId: string
}) {
  const globalAverage = stats.average
  const gameweekHighest = stats.highest

  // GW Points hero tier — live, per-fixture-progressive
  // correct-outcome rate, reusing the season Intuition Rating meter's own
  // thresholds/colors (SKILL_TIERS) rather than deriving new ones from points.
  const liveTier = useMemo(
    () => computeLiveGameweekTier(games, predictions, actualResults, isGameRevealed),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [games, predictions, actualResults, resultsRevealed, simGameStatuses]
  )
  const heroTier = liveTier.guardrailMet ? SKILL_TIERS[getSkillTierIndex(liveTier.pct)] : null

  // Stamp animation: fires exactly once, the moment every fixture in the
  // gameweek has resolved (not on every re-render while already at full
  // time) — same "detect the moment of a transition" shape as the completion
  // pulse in usePredictionEntrance.
  const wasAllResolvedRef = useRef(allFixturesResolved)
  const [stampPlaying, setStampPlaying] = useState(false)

  useEffect(() => {
    const wasAllResolved = wasAllResolvedRef.current
    wasAllResolvedRef.current = allFixturesResolved
    if (wasAllResolved || !allFixturesResolved) return

    // Triggered from a rAF callback, not the effect body: post-paint is the
    // right timing for an entrance animation, and setState from a callback
    // doesn't cascade a render. The clear-timer starts inside the
    // same callback so the on/off window can't drift by a frame. No JS
    // reduced-motion guard — GameweekGrid.css already has `animation: none`
    // under `prefers-reduced-motion: reduce`, so the class is a visual no-op
    // there anyway.
    let timer: ReturnType<typeof setTimeout> | undefined
    const frameId = requestAnimationFrame(() => {
      setStampPlaying(true)
      timer = setTimeout(() => setStampPlaying(false), STAMP_ANIMATION_MS)
    })
    return () => {
      cancelAnimationFrame(frameId)
      if (timer !== undefined) clearTimeout(timer)
    }
  }, [allFixturesResolved])

  // Brief pulse each time the live Goalfest counter (under the GW Points
  // card) increases — separate from the continuous glow it no longer has;
  // never pulses on a decrease (an outcome flipping to incorrect) or once
  // settled.
  const [goalfestPulsing, setGoalfestPulsing] = useState(false)
  const prevGoalfestBonusRef = useRef(goalfestLiveBonus)
  useEffect(() => {
    const prev = prevGoalfestBonusRef.current
    prevGoalfestBonusRef.current = goalfestLiveBonus
    if (allFixturesResolved || goalfestLiveBonus <= prev) return

    setGoalfestPulsing(true)
    const timer = setTimeout(() => setGoalfestPulsing(false), GOALFEST_PULSE_MS)
    return () => clearTimeout(timer)
  }, [goalfestLiveBonus, allFixturesResolved])

  // Hero value roll-up (the Motion spec: "rolls up on load and on
  // change") — reuses the homepage total-points count-up primitives
  // (scoreCountUp.ts) with its own once-per-load flag, since this animates a
  // different value on a different component's mount lifecycle.
  const [displayedPoints, setDisplayedPoints] = useState(() =>
    !hasGwPointsCountUpPlayed() && !prefersReducedMotion() ? 0 : gameweekPointsSoFar
  )
  // What is on screen right now, mirrored into a ref so the animation below can
  // pick up from it. Deliberately NOT "the last target": an animation that is
  // cancelled before it finishes leaves the display where it stopped, and
  // starting the next one from a value that was never reached skips the gap.
  //
  // Under StrictMode that difference is the whole behaviour, not an edge case.
  // React double-invokes every effect on mount — run, clean up, run again — so a
  // ref advanced to the target on the first (cancelled) run makes the second see
  // `from === to` and animate nothing at all, freezing the hero at 0. Invisible
  // while points could only change *after* mount (a dev "reveal"); permanent now that a page
  // opened mid-gameweek renders non-zero points on its very first paint.
  const displayedRef = useRef(displayedPoints)

  useEffect(() => {
    markGwPointsCountUpPlayed()
    const from = displayedRef.current
    const to = gameweekPointsSoFar
    if (from === to) return

    // Under reduced motion there is nothing to animate — the rendered value is
    // derived straight from `gameweekPointsSoFar` below instead of being pushed
    // through state. `markGwPointsCountUpPlayed()` above is
    // load-bearing and must run before this bail-out.
    if (prefersReducedMotion()) return

    let frameId = 0
    const startTime = performance.now()
    const tick = (now: number) => {
      const progress = Math.min((now - startTime) / SCORE_COUNT_UP_MS, 1)
      const value = Math.round(from + easeOutCubic(progress) * (to - from))
      displayedRef.current = value
      setDisplayedPoints(value)
      if (progress < 1) frameId = requestAnimationFrame(tick)
    }
    frameId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frameId)
  }, [gameweekPointsSoFar])

  // The value actually rendered. With reduced motion on, `displayedPoints` is
  // never advanced by the effect above, so the target is read directly — same
  // output as the old synchronous setState, minus the cascading render. (If the
  // OS setting is turned off mid-session the next count-up starts from whatever
  // was last animated, which is the honest answer: it is where the roll-up would
  // have been had it been running.)
  const renderedPoints = prefersReducedMotion() ? gameweekPointsSoFar : displayedPoints

  return {
    globalAverage,
    gameweekHighest,
    // Who to open when Highest is pressed, or null when there is nowhere to go: no identity yet
    // (pre-lock, or nobody scored), or the top scorer is the viewer — their own picks are the page
    // they are already on, so the tile stays plain text rather than becoming a button to here.
    topScorer:
      stats.highestUserId && stats.highestDisplayName && stats.highestUserId !== myUserId
        ? { userId: stats.highestUserId, displayName: stats.highestDisplayName }
        : null,
    heroTier,
    stampPlaying,
    goalfestPulsing,
    renderedPoints,
  }
}

export type GwPointsHero = ReturnType<typeof useGwPointsHero>
